import { useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Dropdown,
  Icon,
  IconButton,
  Input,
  Logo,
  initialsFromName,
} from '../components';
import type { IconName } from '../components';
import { useAuthStore } from '../lib/auth-store';
import { AccountProfileModal } from '../features/auth/AccountProfileModal';
import { ChangePasswordModal } from '../features/auth/ChangePasswordModal';

type NavLeaf = {
  kind: 'leaf';
  key: string;
  label: string;
  icon: IconName;
  /** When path is `null` the item renders but is not yet clickable (Pass 2C). */
  path: string | null;
  badge?: number;
};

type NavGroup = {
  kind: 'group';
  key: string;
  label: string;
  icon: IconName;
  children: Array<{ label: string; path: string | null }>;
};

type NavEntry = NavLeaf | NavGroup | { kind: 'section'; label: string };

/**
 * Sidebar nav definition. In Pass 2B only the "Lead statuses" route
 * (/lead-statuses) is wired to a real page. Every other entry renders as an
 * inert, muted item — Pass 2C swaps the nulls for real paths as placeholder
 * pages land.
 */
const NAV: NavEntry[] = [
  { kind: 'leaf', key: 'dashboard', label: 'Dashboard', icon: 'dashboard', path: '/dashboard' },
  { kind: 'leaf', key: 'leads', label: 'Leads', icon: 'leads', path: '/leads' },
  { kind: 'leaf', key: 'clients', label: 'Clients', icon: 'clients', path: '/clients' },
  { kind: 'leaf', key: 'contacts', label: 'Contacts', icon: 'contacts', path: '/contacts' },
  { kind: 'leaf', key: 'followups', label: 'Follow-ups', icon: 'check', path: '/followups' },
  { kind: 'leaf', key: 'calendar', label: 'Calendar', icon: 'calendar', path: '/calendar' },
  { kind: 'section', label: 'Team' },
  { kind: 'leaf', key: 'staff', label: 'Staff', icon: 'staff', path: '/staff' },
  {
    kind: 'group',
    key: 'hrm',
    label: 'HRM',
    icon: 'hrm',
    children: [
      { label: 'Roles', path: '/hrm/roles' },
      { label: 'Departments', path: '/hrm/departments' },
      { label: 'Shifts', path: '/hrm/shifts' },
    ],
  },
  { kind: 'section', label: 'Configure' },
  {
    kind: 'group',
    key: 'settings',
    label: 'Settings',
    icon: 'settings',
    children: [
      { label: 'Lead statuses', path: '/settings/lead-statuses' },
      { label: 'Lead categories', path: '/settings/lead-categories' },
      { label: 'Contact categories', path: '/settings/contact-categories' },
      { label: 'Sources', path: '/settings/sources' },
      { label: 'Birthday templates', path: '/settings/birthday-templates' },
    ],
  },
];

function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const location = useLocation();

  const initiallyOpen = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const entry of NAV) {
      if (entry.kind === 'group') {
        out[entry.key] = entry.children.some(
          (c) => c.path && location.pathname.startsWith(c.path),
        );
      }
    }
    return out;
  }, [location.pathname]);

  const [open, setOpen] = useState<Record<string, boolean>>(initiallyOpen);

  return (
    <aside className="sidebar" style={{ width: collapsed ? 64 : 232 }}>
      <div className="sidebar-head">
        {!collapsed ? (
          <Logo size={22} />
        ) : (
          <div className="brand-mark" aria-label="NINON">
            N
          </div>
        )}
        <IconButton
          name={collapsed ? 'chevronRight' : 'chevronLeft'}
          onClick={onToggle}
          label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        />
      </div>

      <nav className="sidebar-nav">
        {NAV.map((entry, i) => {
          if (entry.kind === 'section') {
            return !collapsed ? (
              <div key={`section-${i}`} className="nav-section">
                {entry.label}
              </div>
            ) : null;
          }
          if (entry.kind === 'leaf') {
            const disabled = entry.path === null;
            const content = (
              <>
                <Icon name={entry.icon} size={18} />
                {!collapsed && (
                  <span style={{ flex: 1, textAlign: 'left' }}>{entry.label}</span>
                )}
                {!collapsed && entry.badge !== undefined && (
                  <span className="nav-badge">{entry.badge}</span>
                )}
              </>
            );
            if (disabled) {
              return (
                <div
                  key={entry.key}
                  className="nav-item disabled"
                  title={collapsed ? entry.label : undefined}
                  aria-disabled="true"
                >
                  {content}
                </div>
              );
            }
            return (
              <NavLink
                key={entry.key}
                to={entry.path!}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                title={collapsed ? entry.label : undefined}
              >
                {content}
              </NavLink>
            );
          }
          // group
          const isGroupOpen = open[entry.key] ?? false;
          const groupActive = entry.children.some(
            (c) => c.path && location.pathname === c.path,
          );
          return (
            <div key={entry.key}>
              <button
                type="button"
                className={`nav-item ${groupActive ? 'active' : ''}`}
                onClick={() => setOpen((o) => ({ ...o, [entry.key]: !o[entry.key] }))}
                title={collapsed ? entry.label : undefined}
              >
                <Icon name={entry.icon} size={18} />
                {!collapsed && (
                  <span style={{ flex: 1, textAlign: 'left' }}>{entry.label}</span>
                )}
                {!collapsed && (
                  <Icon
                    name="chevronRight"
                    size={14}
                    style={{
                      transform: isGroupOpen ? 'rotate(90deg)' : 'none',
                      transition: 'transform .15s',
                    }}
                  />
                )}
              </button>
              {isGroupOpen && !collapsed && (
                <div className="nav-subgroup">
                  {entry.children.map((c) => {
                    const disabled = c.path === null;
                    if (disabled) {
                      return (
                        <div
                          key={c.label}
                          className="nav-sub disabled"
                          aria-disabled="true"
                        >
                          {c.label}
                        </div>
                      );
                    }
                    return (
                      <NavLink
                        key={c.label}
                        to={c.path!}
                        className={({ isActive }) => `nav-sub ${isActive ? 'active' : ''}`}
                      >
                        {c.label}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

    </aside>
  );
}

function Topbar() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <header className="topbar">
      <div className="search">
        <Input icon="search" placeholder="Search leads, clients, contacts…" disabled />
      </div>
      <div className="spacer" />
      <div className="right">
        <IconButton name="help" label="Help" disabled />
        <IconButton name="bell" label="Notifications" disabled />
        <div className="divider" />
        <Dropdown
          trigger={
            <button
              type="button"
              className="user user-trigger"
              aria-label="Open account menu"
            >
              <Avatar
                initials={initialsFromName(user?.full_name ?? user?.email ?? null)}
                color="var(--green-700)"
                size="sm"
              />
              <div className="user-meta">
                <div className="name">{user?.full_name ?? user?.email ?? ''}</div>
                <div className="role">{user?.roles?.[0] ?? 'Signed in'}</div>
              </div>
              <Icon name="chevronDown" size={14} style={{ color: 'var(--ink-3)' }} />
            </button>
          }
        >
          <div className="dropdown-account-head">
            <div className="dropdown-account-name">
              {user?.full_name ?? user?.email ?? ''}
            </div>
            {user?.email && user?.full_name && (
              <div className="dropdown-account-sub">{user.email}</div>
            )}
          </div>
          <div className="dropdown-divider" />
          <button
            type="button"
            className="dropdown-item"
            onClick={() => setProfileOpen(true)}
          >
            <Icon name="staff" size={14} />
            My profile
          </button>
          <button
            type="button"
            className="dropdown-item"
            onClick={() => setPasswordOpen(true)}
          >
            <Icon name="settings" size={14} />
            Change password
          </button>
          <div className="dropdown-divider" />
          <button
            type="button"
            className="dropdown-item danger"
            onClick={onLogout}
          >
            <Icon name="logout" size={14} />
            Sign out
          </button>
        </Dropdown>
      </div>

      <AccountProfileModal
        open={profileOpen}
        user={user}
        onClose={() => setProfileOpen(false)}
      />
      <ChangePasswordModal
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
      />
    </header>
  );
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="app-shell">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      <div className="app-main">
        <Topbar />
        <main className="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
